/* eslint-disable no-return-assign,no-unused-vars,camelcase */
/* global G, PLATFORM */
import map from 'lib/sequence/model/api/map'
import find from 'lib/sequence/component/children/find'
import sequenceComponentState from 'lib/sequence/component/state'
import { hide, show } from 'lib/sequence/component/state/hidden'
import { asyncPipeSpread, bulk } from 'lib/util'
import { get, set } from 'lib/sequence/component/state/value'
import { disable } from 'lib/sequence/component/state/disabled'
import { settings } from 'app/_shared/session'

const WIDE_LOGIN_CLASS = 'wideLogin'
const SECONDARY_LOGIN_CLASS = 'secondaryLogin'

const bulkHide = bulk(hide)
const bulkShow = bulk(show)

const shouldUseOAuth = module => settings?.oauth?.enabled && module[G.MODEL][G.STATE].oauth

const {
  set: setClasses,
} = sequenceComponentState('classes')

const {
  set: setColor,
} = sequenceComponentState('color')

const {
  set: focus,
} = sequenceComponentState('focused')

/**
 * Copies the properties of `data` to the application's session state object.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @param {Object} data                 an object containing the properties to be copied
 */
export const setSessionStateData = (module, data = {}) => {
  const sessionState = module[G.ADAPTER][G.SESSION][G.STATE]
  Object.keys(data).forEach(key => sessionState[key] = data[key])
  // setKey(data, G.DATA, sessionState)
}

/**
 * Returns the state of the current session.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {Object}                    an object representing the current session's state
 */
export const getSessionStateData = (module) => {
  const sessionState = module[G.ADAPTER][G.SESSION][G.STATE]
  // const data = sessionState[G.USER] || {}
  // setKey(data, G.DATA, sessionState)
  // return data
  return sessionState
}

/**
 * Hides the registration button if the `suppressRegistration` setting is `true`.
 *
 * @type {Gaia.AppModule.ActionPartial}
 */
const toggleRegistrationDisplay = module => async (components, ...args) => {
  const { textRegister, btnRegister } = components

  settings?.suppressRegistration && bulkHide(textRegister, btnRegister)

  return args
}

/**
 * Toggles the display of the remember me checkbox.
 *
 * @type {Gaia.AppModule.ActionPartial}
 */
const toggleCheckboxDisplay = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { sticky, textRegister, btnRegister } = components

  PLATFORM === 'mobile'
    && bulkHide(sticky, textRegister, btnRegister)
    && (model[G.DATA].sticky = true)

  return [components, ...args]
}

/**
 * Toggles the display of the OAuth log-in button and its "Or" label above if oauth is enabled for
 * the current tenant.
 *
 * @type {Gaia.AppModule.ActionPartial}
 */
const toggleOAuthButtonDisplay = module => async (components, ...args) => {
  const { labelOr, btnOAuth } = components

  settings?.oauth?.enabled
    ? bulkShow(labelOr, btnOAuth)
    : bulkHide(labelOr, btnOAuth)

  return [components, ...args]
}

/**
 * Toggles the display of the password field and the Continue and Log in buttons depending on
 * whether the username field has a valid value.
 *
 * @type {Gaia.AppModule.ActionPartial}
 */
const togglePasswordAndButtonsDisplay = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { username, password, btnContinue, btnLogin } = components
  const { username: usernameData } = model[G.DATA]
  const { username: sessionUsername = usernameData } = getSessionStateData(module)
  const { passwordGenerated } = model[G.STATE]

  const displayPredicate = !shouldUseOAuth(module)
    && (settings.singleStepLogin || (sessionUsername && !passwordGenerated))

  displayPredicate
    ? focus(password) && show(password)
    : hide(password) && focus(username)

  displayPredicate
    ? show(btnLogin) && hide(btnContinue)
    : show(btnContinue) && hide(btnLogin)

  // TODO: Not used anymore after moving the log-in button from actions to content, because of the
  //        new "Sign in with OAuth" button. But maybe we could keep the button in the actions if
  //        OAuth is disabled?
  //
  // if (PLATFORM === 'mobile') {
  //   const { component } = components
  //   const {
  //     btnLogin: mobileBtnLogin,
  //     btnContinue: mobileBtnContinue,
  //   } = find(component[G.ACTIONS][0])
  //
  //   displayPredicate
  //     ? show(mobileBtnLogin) && hide(mobileBtnContinue)
  //     : show(mobileBtnContinue) && hide(mobileBtnLogin)
  // }

  return [components, ...args]
}

/**
 * Maps the model or session username to the username field.
 *
 * @type {Gaia.AppModule.ActionPartial}
 */
const mapUsername = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { username: usernameData } = model[G.DATA]
  const { username: sessionUsername } = getSessionStateData(module)
  const { form, options, username } = components

  const bulkMapModel = bulk(map(model))

  !usernameData && bulkMapModel(form, options)
  sessionUsername && !get(username) && set(username, sessionUsername)

  return [components, ...args]
}

/**
 * Disables the email validator of the username field and changes its labels to 'Username'.
 *
 * @param {Gaia.AppModule.Spec} module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const toggleUsernameEmailValidator = module => async (components, ...args) => {
  if (settings.allowNonEmailUsernames) {
    const model = module[G.MODEL]
    const { username } = model[G.CHILDREN]
    const { username: usernameField } = components

    const emptyValidator = username[G.VALIDATOR].find(validator => validator._name === 'notEmpty')
    const emailValidator = username[G.VALIDATOR].find(validator => validator._name === 'isEmail')

    const usernameFieldConfig = usernameField[G.CONFIGURATION]
    const emptyValidatorConfig = emptyValidator[G.CONFIGURATION]

    const tUsername = { ns: 'user', _key: 'label.username' }

    usernameFieldConfig.t.label.options = tUsername
    emptyValidatorConfig.t.options.label = tUsername
    disable(emailValidator)
  }
  return [components, ...args]
}

/**
 * Toggles the display of the legacy application block according to the `showLegacyAppText` setting.
 *
 * @type {Gaia.AppModule.ActionPartial}
 */
const toggleLegacyAppBlockDisplay = module => async (components, ...args) => {
  const { legacyAppBlock } = components

  PLATFORM === 'web' && (settings.showLegacyAppText ? show(legacyAppBlock) : hide(legacyAppBlock))

  return [components, ...args]
}

/**
 * Toggles the action's ui layout according to the `wideLayoutLogin` and `secondaryColorLogin`
 * settings.
 *
 * @type {Gaia.AppModule.ActionPartial}
 */
const toggleLayoutClasses = module => async (components, ...args) => {
  const { component, btnContinue, btnLogin } = components
  const classes = component[G.PROPS].classes || []

  settings.wideLayoutLogin
    && !classes.includes(WIDE_LOGIN_CLASS)
    && classes.push(WIDE_LOGIN_CLASS)

  settings.secondaryColorLogin
    && setColor(btnContinue, 'secondary')
    && setColor(btnLogin, 'secondary')

  setClasses(component, classes)

  return [components, ...args]
}

/**
 * Hides or shows elements from the login screen depending on whether the user exists and is active.
 *
 * @param {Gaia.AppModule.Spec} module - application module
 * @returns {function}    a function expecting the index component
 */
export default module => component => async (...args) => await asyncPipeSpread(
  toggleLayoutClasses(module),
  toggleLegacyAppBlockDisplay(module),
  toggleUsernameEmailValidator(module),
  mapUsername(module),
  togglePasswordAndButtonsDisplay(module),
  toggleOAuthButtonDisplay(module),
  toggleCheckboxDisplay(module),
  toggleRegistrationDisplay(module),
)(find(component), ...args)
